import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ClinicalDecisionSupport = ({ recommendations, patientFactors, onApplyRecommendation }) => {
  const getRecommendationIcon = (type) => {
    switch (type) {
      case 'dosing':
        return 'Calculator';
      case 'renal':
        return 'Droplet';
      case 'pediatric':
        return 'Baby';
      case 'geriatric':
        return 'Users';
      case 'interaction':
        return 'AlertTriangle';
      default:
        return 'Info';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
          <Icon name="Lightbulb" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-foreground">Clinical Decision Support</h2>
          <p className="text-sm text-muted-foreground">Evidence-based dosing recommendations</p>
        </div>
      </div>
      <div className="bg-muted rounded-lg p-4 mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Patient-Specific Factors</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <p className="text-muted-foreground mb-1">Age</p>
            <p className="font-medium text-foreground">{patientFactors?.age} years</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">Weight</p>
            <p className="font-medium text-foreground">{patientFactors?.weight} kg</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">eGFR</p>
            <p className="font-medium text-foreground">{patientFactors?.egfr} mL/min</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">Liver Function</p>
            <p className="font-medium text-foreground">{patientFactors?.liverFunction}</p>
          </div>
        </div>
      </div>
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-foreground">Active Recommendations</h3>
        {recommendations?.map((rec, index) => (
          <div key={index} className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg flex-shrink-0">
                <Icon name={getRecommendationIcon(rec?.type)} size={16} color="var(--color-primary)" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-foreground mb-1">{rec?.title}</h4>
                <p className="text-xs text-muted-foreground mb-2">{rec?.description}</p>
              </div>
            </div>

            {rec?.calculation && (
              <div className="bg-card rounded-lg p-3 mb-3">
                <p className="text-xs font-medium text-foreground mb-2">Calculation:</p>
                <div className="font-mono text-xs text-muted-foreground space-y-1">
                  {rec?.calculation?.steps?.map((step, idx) => (
                    <p key={idx}>{step}</p>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-border">
                  <p className="text-sm font-semibold text-primary">
                    Recommended Dose: {rec?.calculation?.result}
                  </p>
                </div>
              </div>
            )}

            {rec?.guidelines && (
              <div className="bg-card rounded-lg p-3 mb-3">
                <p className="text-xs font-medium text-foreground mb-2">Clinical Guidelines:</p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                  {rec?.guidelines?.map((guideline, idx) => (
                    <li key={idx}>{guideline}</li>
                  ))}
                </ul>
              </div>
            )}

            {rec?.references && (
              <div className="bg-card rounded-lg p-3 mb-3">
                <p className="text-xs font-medium text-foreground mb-2">References:</p>
                <p className="text-xs text-muted-foreground">{rec?.references}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                variant="default"
                size="sm"
                iconName="Check"
                iconPosition="left"
                onClick={() => onApplyRecommendation(rec)}
              >
                Apply Recommendation
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="ExternalLink"
                iconPosition="left"
              >
                View Full Guidelines
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ClinicalDecisionSupport;